import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/utils/api';
import { Plus, X, CheckCircle2, Circle, AlertTriangle, MessageCircle, FileText, ShieldCheck, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Tenant Onboarding & Experience
// Route: /tenant-onboarding (org admins)
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  not_started:        { label: 'Not started',        bg: 'bg-[#F1F4F7]',  text: 'text-[#4A5263]' },
  in_progress:        { label: 'In progress',         bg: 'bg-amber-50',   text: 'text-amber-800' },
  ready_to_move_in:   { label: 'Ready to move in',    bg: 'bg-emerald-50', text: 'text-emerald-700' },
  completed:          { label: 'Completed',           bg: 'bg-emerald-100',text: 'text-emerald-800' },
  escalated:          { label: 'Escalated',           bg: 'bg-red-50',     text: 'text-red-700' },
};

const TASK_ICONS = {
  legal: ShieldCheck, financial: FileText, kyc: ShieldCheck,
  compliance: FileText, operations: CheckCircle2, experience: MessageCircle,
};

export default function TenantOnboarding() {
  const [list, setList] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    try {
      const [o, t] = await Promise.all([api.onboardingList(), api.getTenants()]);
      setList(o.data); setTenants(t.data || []);
    } catch (e) { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  if (selectedId) return <OnboardingDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} />;

  return (
    <Layout>
      <div className="max-w-[1240px] mx-auto px-6 py-8" data-testid="tenant-onboarding-page">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0E1320] text-white text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase">
              <CheckCircle2 className="h-3 w-3" /> Tenant Onboarding
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-[#0E1320]">Digital move-in journeys</h1>
            <p className="mt-1.5 text-[14px] text-[#4A5263] max-w-2xl">
              Welcome packets · checklists · e-sign · KYC · acknowledgments · escalations — bilingual AR/EN, mobile-first.
            </p>
          </div>
          <button onClick={() => setOpenCreate(true)} disabled={tenants.length === 0}
            data-testid="onboarding-create-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00734A] text-white text-[13px] font-semibold hover:bg-[#005F3D] disabled:opacity-60 transition-colors">
            <Plus className="h-4 w-4" /> New onboarding
          </button>
        </div>

        <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
          {list.length === 0 ? (
            <div className="text-center py-16 px-6">
              <CheckCircle2 className="h-10 w-10 mx-auto text-[#71777E]" strokeWidth={1.4} />
              <h3 className="mt-3 text-[15px] font-semibold text-[#0E1320]">No onboardings yet</h3>
              <p className="mt-1 text-[12.5px] text-[#4A5263]">{tenants.length === 0 ? 'Add a tenant first, then start their move-in journey.' : 'Kick off a new tenant onboarding to start the checklist.'}</p>
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-[#FBFCFD] text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
                <tr>
                  <th className="text-start px-4 py-3 font-medium">Tenant</th>
                  <th className="text-start px-4 py-3 font-medium">Move-in target</th>
                  <th className="text-start px-4 py-3 font-medium">Progress</th>
                  <th className="text-start px-4 py-3 font-medium">Tasks</th>
                  <th className="text-start px-4 py-3 font-medium">Acknowledgments</th>
                  <th className="text-start px-4 py-3 font-medium">Status</th>
                  <th className="text-end px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {list.map(o => {
                  const s = STATUS_STYLE[o.status] || STATUS_STYLE.not_started;
                  return (
                    <tr key={o.id} className="border-t border-[#F1F4F7] hover:bg-[#FBFCFD] cursor-pointer" onClick={() => setSelectedId(o.id)} data-testid={`onboarding-row-${o.id}`}>
                      <td className="px-4 py-3 text-[#0E1320] font-medium">{o.tenant_name}</td>
                      <td className="px-4 py-3 text-[#4A5263] reva-mono text-[12px]">{o.target_move_in || '—'}</td>
                      <td className="px-4 py-3 w-[200px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-[#F1F4F7] overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${o.progress || 0}%` }} />
                          </div>
                          <span className="text-[11px] reva-mono text-[#4A5263] w-9 text-right">{o.progress || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#4A5263] reva-mono">{o.tasks_done}/{o.tasks_total}</td>
                      <td className="px-4 py-3 text-[#4A5263] reva-mono">{o.acknowledgments_count || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${s.bg} ${s.text} text-[11px] font-medium`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-3 text-end"><ChevronRight className="h-4 w-4 text-[#71777E]" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {openCreate && (
        <CreateOnboardingModal tenants={tenants} onClose={() => setOpenCreate(false)} onCreated={(id) => { setOpenCreate(false); setSelectedId(id); }} />
      )}
    </Layout>
  );
}

// ─── Create modal ───────────────────────────────────────────────────────
function CreateOnboardingModal({ tenants, onClose, onCreated }) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id || '');
  const [moveIn, setMoveIn] = useState('');
  const [lang, setLang] = useState('en');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.onboardingCreate({ tenant_id: tenantId, target_move_in: moveIn || null, language: lang });
      toast.success('Onboarding created');
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-[#0E1320]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6" data-testid="onboarding-create-modal">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold text-[#0E1320]">Start tenant onboarding</h2>
          <button type="button" onClick={onClose} className="text-[#71777E] hover:text-[#0E1320]"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="text-[12px] text-[#4A5263]">Tenant</span>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} required data-testid="onboarding-create-tenant"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[14px]">
              {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name || t.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[12px] text-[#4A5263]">Move-in target</span>
            <input type="date" value={moveIn} onChange={(e) => setMoveIn(e.target.value)} data-testid="onboarding-create-move-in"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[14px]" />
          </label>
          <label className="block">
            <span className="text-[12px] text-[#4A5263]">Welcome language</span>
            <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="onboarding-create-lang"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[14px]">
              <option value="en">English</option>
              <option value="ar">العربيّة</option>
            </select>
          </label>
        </div>
        <button disabled={busy || !tenantId} type="submit" data-testid="onboarding-create-submit"
          className="mt-5 w-full h-11 rounded-xl bg-[#00734A] hover:bg-[#005F3D] text-white text-[14px] font-semibold disabled:opacity-60">
          {busy ? 'Creating…' : 'Create onboarding'}
        </button>
      </form>
    </div>
  );
}

// ─── Detail view ────────────────────────────────────────────────────────
function OnboardingDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.onboardingGet(id); setData(data); }
    catch (e) { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'pending' : 'done';
    try {
      const { data: updated } = await api.onboardingUpdateTask(id, taskId, { status: newStatus });
      setData(prev => ({ ...prev, ...updated }));
    } catch (e) { toast.error('Update failed'); }
  };

  const acknowledge = async (kind) => {
    try {
      const { data: updated } = await api.onboardingAcknowledge(id, { kind });
      toast.success(`${kind} acknowledged`);
      setData(prev => ({ ...prev, ...updated }));
      load();
    } catch (e) { toast.error('Failed'); }
  };

  if (!data) return <Layout><div className="max-w-[1240px] mx-auto px-6 py-8 text-[#71777E]">Loading…</div></Layout>;

  const s = STATUS_STYLE[data.status] || STATUS_STYLE.not_started;

  return (
    <Layout>
      <div className="max-w-[1240px] mx-auto px-6 py-8" data-testid={`onboarding-detail-${id}`}>
        <button onClick={onBack} className="text-[12px] text-[#4A5263] hover:text-[#0E1320] mb-4" data-testid="onboarding-detail-back">← All onboardings</button>

        {/* Welcome card */}
        <div className="rounded-2xl border border-[#E6EAF0] bg-gradient-to-br from-[#00734A] to-[#005F3D] text-white p-6 mb-6" data-testid="onboarding-welcome-card">
          <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] opacity-80">Welcome packet</div>
          <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">Welcome, {data.tenant_name}</h1>
          <p className="mt-2 text-[14px] opacity-90 max-w-2xl">{data.language === 'ar' ? data.welcome_message_ar : data.welcome_message_en}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-[12px]">
            <span className={`px-2 py-0.5 rounded-full ${s.bg} ${s.text} font-medium`}>{s.label}</span>
            <span className="reva-mono opacity-80">{data.progress}% complete · {data.tasks_done}/{data.tasks_total} tasks</span>
            {data.target_move_in && <span className="reva-mono opacity-80">· move-in {data.target_move_in}</span>}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 rounded-full bg-[#F1F4F7] overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${data.progress}%` }} />
          </div>
        </div>

        {/* Tasks */}
        <h2 className="text-[15px] font-semibold text-[#0E1320] mb-3">Checklist</h2>
        <div className="space-y-2 mb-8">
          {data.tasks.map(t => {
            const Icon = TASK_ICONS[t.category] || Circle;
            const done = t.status === 'done';
            return (
              <button key={t.id} onClick={() => toggleTask(t.id, t.status)} data-testid={`onboarding-task-${t.id}`}
                className={`w-full flex items-start gap-3 text-start px-4 py-3 rounded-xl border transition-colors ${
                  done ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-[#E6EAF0] hover:border-[#0E1320]'
                }`}>
                <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                  done ? 'bg-emerald-500 text-white' : 'border-2 border-[#E6EAF0]'
                }`}>
                  {done && <CheckCircle2 className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[13.5px] font-medium ${done ? 'text-[#0E1320] line-through' : 'text-[#0E1320]'}`}>{t.title_en}</div>
                  <div className="text-[11.5px] text-[#71777E] mt-0.5">{t.title_ar}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Icon className="h-4 w-4 text-[#71777E]" />
                  {t.blocks_move_in && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9.5px] font-bold reva-mono uppercase">Blocker</span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Acknowledgments */}
        <h2 className="text-[15px] font-semibold text-[#0E1320] mb-3">Acknowledgments</h2>
        <div className="grid md:grid-cols-2 gap-3 mb-8">
          {[
            { kind: 'rules', label: 'House rules',         desc: 'Tenant accepts the property house rules.' },
            { kind: 'manual', label: 'Property manual',    desc: 'Tenant has reviewed the property manual.' },
            { kind: 'lease_terms', label: 'Lease terms',   desc: 'Tenant confirms lease terms read & accepted.' },
            { kind: 'policy', label: 'Privacy & policies', desc: 'Tenant accepts platform & data policies.' },
          ].map(a => (
            <button key={a.kind} onClick={() => acknowledge(a.kind)} data-testid={`onboarding-ack-${a.kind}`}
              className="text-start rounded-xl border border-[#E6EAF0] bg-white p-4 hover:border-[#0E1320] transition-colors">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#00734A]" />
                <div className="text-[13px] font-semibold text-[#0E1320]">{a.label}</div>
              </div>
              <p className="mt-1 text-[12px] text-[#4A5263]">{a.desc}</p>
            </button>
          ))}
        </div>

        {/* Activity */}
        {data.acknowledgments?.length > 0 && (
          <div className="rounded-xl border border-[#E6EAF0] bg-white" data-testid="onboarding-activity">
            <div className="px-4 py-3 border-b border-[#F1F4F7]">
              <h3 className="text-[13px] font-semibold text-[#0E1320]">Activity log</h3>
            </div>
            <ul className="divide-y divide-[#F1F4F7]">
              {data.acknowledgments.map((a) => (
                <li key={a.id} className="px-4 py-3 flex items-center gap-3 text-[12.5px]">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-medium reva-mono uppercase tracking-[0.12em]">{a.kind}</span>
                  <span className="text-[#4A5263]">acknowledged by {a.signature_name || a.acknowledged_by}</span>
                  <span className="ml-auto text-[11px] text-[#71777E] reva-mono">{a.acknowledged_at?.slice(0, 19).replace('T', ' ')}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}
